import { AxiosError, AxiosResponse } from 'axios';

import { ModelOptions } from '../types';

import { Request } from './request';
import type { IdType } from './systemColumns.type';

const handleData = <TData = unknown>(res: AxiosResponse<TData>): TData => res.data;
function handleError<TError = AxiosError | any>(error: TError): never {
    console.error(error);
    throw error;
}
const DEFAULT_TIMEOUT = Number(process.env.TIMEOUT) || 12000; // Default timeout is 12 seconds
class Api<T> extends Request {
    url: string;
    timeout: number;

    constructor(url: string, timeout: number = DEFAULT_TIMEOUT) {
        super();
        this.url = url;
        this.timeout = timeout;
    }

    getSchema() {
        return this.request
            .get(`${this.url}/schema`, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    get(params: Partial<T> = {}, options?: ModelOptions) {
        return this.request
            .get(`${this.url}`, { params: { ...params, ...options }, timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    getById(id: string | number, params?: Partial<T>) {
        return this.request
            .get(`${this.url}/${id}`, { params, timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    getOne(params: IdType, body?: any) {
        return this.request
            .post(`${this.url}-get-one`, { params, body }, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    post(body: Partial<T>) {
        return this.request
            .post(`${this.url}`, body, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    put(body: Partial<T>) {
        return this.request
            .put(`${this.url}`, body, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    remove(id: IdType) {
        return this.request
            .delete(`${this.url}/${id}`, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }

    softDelete({ _id }: { _id: IdType }) {
        const body = { _id: Number(_id) };
        return this.request
            .put(`${this.url}/soft-delete`, body, { timeout: this.timeout })
            .then(handleData)
            .catch(handleError);
    }
}

export default Api;

export { handleData, handleError };
